import ArmorSet from '../../searcher/models/ArmorSet'
import SearchConstraints from '../../searcher/models/SearchConstraints'
import StaticEquipmentData from '../../data-provider/models/equipment/StaticEquipmentData'
import StaticSkillData from '../../data-provider/models/skills/StaticSkillData'
import { search } from '../../searcher/searcher.module'
import { getGlobalSettings } from './global-settings.component'
import { moreSkillsIterator, renderMoreSkills, renderResults } from './search-results.component'
import SkillActivation from '../../data-provider/models/skills/SkillActivation'
import UserEquipmentSettings from '../../data-provider/models/user/UserEquipmentSettings'
import EquipmentMin from '../../data-provider/models/equipment/EquipmentMin'
import { getSkillActivations, resetSkillActivations, updateNegativeSkillsVisibility } from './picker.component';
import GlobalSettings from '../models/GlobalSettings';

const checkFixedEquipmentHR = (
  equData: StaticEquipmentData,
  globalSettings: GlobalSettings
): boolean => {
  const pins = UserEquipmentSettings.Instance.pins;
  const armorHRlimit = globalSettings.armorHRlimit;
  const categoryNames = ['头', '胴', '腕', '腰', '脚'];
  const violations: string[] = [];

  for (let i = 0; i < pins.length; i++) {
    const pin = pins[i];
    if (!pin) continue;
    const pieces = equData.armor[i];
    const found = pieces.find(p => p.name === pin.name);
    if (found && found.hrlimit > armorHRlimit) {
      violations.push(`固定的${categoryNames[i]}装备“${pin.name}”`);
    }
  }

  if (violations.length > 0) {
    alert(`以下固定装备不满足设置的生产条件：\n${violations.join('\n')}`);
    return false;
  }
  return true;
};

const pinsOrExclusionsActive = (pins: (EquipmentMin | undefined)[], exclusions: EquipmentMin[][]): boolean => {
  const hasPins = pins.some(p => p !== undefined);
  const hasExclusions = exclusions.some(eL => eL.length > 0);
  const hasDecoExclusions = UserEquipmentSettings.Instance.decorationExclusions.length > 0;
  return hasPins || hasExclusions || hasDecoExclusions;
};

const arrangeSearchData = (providedSkillActivations?: SkillActivation[]) => {
  // build params
  const globalSettings = getGlobalSettings()
  let skillActivations = providedSkillActivations !== undefined ? providedSkillActivations : getSkillActivations();
  if (!globalSettings.enableNegativeSkills) {
    skillActivations = skillActivations.filter(s => s.isPositive)
  }
  // return if no skill selected
  if (skillActivations.length === 0) {
    return
  }

  // sanitize activation input to only include highest version of each skill
  const sanitizedSkillActivations = skillActivations
    .filter((thisAct, i) => {
      return skillActivations.every((compareAct, j) => {
        if (i === j) return true
        if (thisAct.requiredSkill !== compareAct.requiredSkill) return true
        if (thisAct.requiredPoints > 0) {
          return thisAct.requiredPoints >= compareAct.requiredPoints;
        } else {
          return thisAct.requiredPoints <= compareAct.requiredPoints;
        }
      })
    })

  // create search params
  const searchParams: SearchConstraints = {
    weaponSlots: globalSettings.weaponSlots,
    armorType: globalSettings.armorType,
    armorHRlimit: globalSettings.armorHRlimit,
    decoHRlimit: globalSettings.decoHRlimit,
    limit: Math.min(Math.max(globalSettings.limit, 1), 1000),
    skillActivations: sanitizedSkillActivations,
    pins: UserEquipmentSettings.Instance.pins,
    exclusions: UserEquipmentSettings.Instance.exclusions,
    defenseMode: globalSettings.defenseMode,
  }

  return searchParams
}

const performSearchWithBannerCheck = (
  equData: StaticEquipmentData,
  skillData: StaticSkillData,
  searchParams: SearchConstraints,
): { result: ArmorSet[]; shouldShowBanner: boolean } => {
  let result = search(equData.armor, equData.decorations, [], searchParams, skillData);
  let shouldShowBanner = false;

  if (result.length === 0) {
    const relaxedParams: SearchConstraints = {
      ...searchParams,
      limit: 1,
      pins: searchParams.pins.map(() => undefined),
      exclusions: searchParams.exclusions.map(() => []),
    };

    const savedDecoExclusions = UserEquipmentSettings.Instance.decorationExclusions;
    UserEquipmentSettings.Instance.decorationExclusions = [];

    const relaxedResult = search(equData.armor, equData.decorations, [], relaxedParams, skillData);

    UserEquipmentSettings.Instance.decorationExclusions = savedDecoExclusions;

    if (relaxedResult.length > 0) {
      shouldShowBanner = true;
    }
  }

  return { result, shouldShowBanner };
};

const searchLogic = (equData: StaticEquipmentData, skillData: StaticSkillData) => {

  const resultContainer = document.getElementById('search-results')!;
  resultContainer.innerHTML = '<div class="loading-spinner"></div>';

  setTimeout(() => {
    let skillActivations = getSkillActivations();
    const globalSettings = getGlobalSettings();
    if (!globalSettings.enableNegativeSkills) {
      skillActivations = skillActivations.filter(s => s.isPositive);
    }
    if (skillActivations.length === 0) {
      alert('请至少选择1个技能');
      resultContainer.innerHTML = '';
      return;
    }
    if (checkConflictingSkills(skillActivations, skillData.skillName)) {
      resultContainer.innerHTML = '';
      return;
    }
    if (!checkFixedEquipmentHR(equData, globalSettings)) {
      resultContainer.innerHTML = '';
      return;
    }
    const searchParams = arrangeSearchData(skillActivations);
    if (!searchParams) {
      alert('请至少选择1个技能');
      resultContainer.innerHTML = '';
      return;
    }
    const { result, shouldShowBanner } = performSearchWithBannerCheck(equData, skillData, searchParams);
    renderResults(result, skillData, searchParams, shouldShowBanner);
  }, 0);
};

const moreSkillsLogic = async (equData: StaticEquipmentData, skillData: StaticSkillData) => {
  const resultContainer = document.getElementById('search-results')!;
  resultContainer.innerHTML = '<div class="loading-spinner"></div>';
  let skillActivations = getSkillActivations();
  const globalSettings = getGlobalSettings();
  if (!globalSettings.enableNegativeSkills) {
    skillActivations = skillActivations.filter(s => s.isPositive);
  }
  if (skillActivations.length === 0) {
    alert('请至少选择1个技能');
    resultContainer.innerHTML = '';
    return;
  }
  if (checkConflictingSkills(skillActivations, skillData.skillName)) {
    resultContainer.innerHTML = '';
    return;
  }
  if (!checkFixedEquipmentHR(equData, globalSettings)) {
    resultContainer.innerHTML = '';
    return;
  }
  const searchParams = arrangeSearchData(skillActivations);
  if (!searchParams) {
    alert('请至少选择1个技能');
    resultContainer.innerHTML = '';
    return;
  }

  const { shouldShowBanner } = performSearchWithBannerCheck(equData, skillData, searchParams);

  const aquirableSkills: SkillActivation[] = [];
  const outputIterator = moreSkillsIterator(skillData.skillActivation);
  const enableNegative = globalSettings.enableNegativeSkills || false;

  for (const actMap of skillData.skillActivation) {
    const sActs = actMap[1];
    const processedActs = sActs
      .filter(act => enableNegative ? true : act.requiredPoints >= 0)
      .filter(act => !searchParams.skillActivations.map(x => x.id).includes(act.id))
      .filter(act => !searchParams.skillActivations.find(x => act.requiredSkill === x.requiredSkill && act.requiredPoints < x.requiredPoints))
      .sort((a, b) => a.requiredPoints - b.requiredPoints);

    let breakFlag = false;
    for (const act of processedActs) {
      outputIterator.next();
      if (breakFlag) continue;

      const newParams: SearchConstraints = {
        ...searchParams,
        limit: 1,
        skillActivations: searchParams.skillActivations.concat(act),
      };

      const r = await new Promise<ArmorSet[]>((resolve) => {
        setTimeout(() => {
          resolve(search(equData.armor, equData.decorations, [], newParams, skillData));
        });
      });

      if (r.length === 0) breakFlag = true;
      else aquirableSkills.push(act);
    }
  }

  renderMoreSkills(aquirableSkills, shouldShowBanner);
};

const resetLogic = () => {
  resetSkillActivations()
}

const checkConflictingSkills = (skillActivations: SkillActivation[], skillNameMap: Map<number, string>): boolean => {
  const skillMap = new Map<number, { hasPositive: boolean; hasNegative: boolean; positiveName?: string; negativeName?: string }>();
  for (const act of skillActivations) {
    if (!skillMap.has(act.requiredSkill)) {
      skillMap.set(act.requiredSkill, { hasPositive: false, hasNegative: false });
    }
    const entry = skillMap.get(act.requiredSkill)!;
    if (act.isPositive) {
      entry.hasPositive = true;
      entry.positiveName = act.name;
    } else {
      entry.hasNegative = true;
      entry.negativeName = act.name;
    }
  }
  for (const [skillId, flags] of skillMap) {
    if (flags.hasPositive && flags.hasNegative) {
      const skillName = skillNameMap.get(skillId) || `技能ID ${skillId}`;
      alert(`所选技能矛盾：技能“${skillName}”同时选择了“${flags.positiveName}”和“${flags.negativeName}”，请检查。`);
      return true;
    }
  }
  return false;
};

/** attach handlers for control buttons */
export const attachControlListeners = (equData: StaticEquipmentData, skillData: StaticSkillData) => {
  const searchBtn = document.getElementById('search-btn') as HTMLButtonElement
  const moreSkillsBtn = document.getElementById('more-btn') as HTMLButtonElement
  const resetBtn = document.getElementById('reset-btn') as HTMLButtonElement

  searchBtn.addEventListener('click', () => {
    searchLogic(equData, skillData)
  })
  moreSkillsBtn.addEventListener('click', () => {
    moreSkillsLogic(equData, skillData)
  })
  resetBtn.addEventListener('click', () => {
    resetLogic()
  })
  const negativeCheckbox = document.getElementById('enable-negative-skills') as HTMLInputElement;
  if (negativeCheckbox) {
    updateNegativeSkillsVisibility(negativeCheckbox.checked);

    negativeCheckbox.addEventListener('change', () => {
      const show = negativeCheckbox.checked;
      updateNegativeSkillsVisibility(show);
    });
  }
  const negativeCheckboxBottom = document.getElementById('enable-negative-skills-bottom') as HTMLInputElement;
  if (negativeCheckboxBottom) {
    negativeCheckboxBottom.addEventListener('change', () => {
      const show = negativeCheckboxBottom.checked;
      updateNegativeSkillsVisibility(show);
    });
  }
}
